import { Pencil } from 'lucide-react'
import type { Campaign, Target } from '@/api/client'
import { toneLabel } from '@/lib/coupang'
import { useAffiliateAvatars, useAffiliateCategories } from '@/lib/queries'
import { TONE_CLASS } from '@/lib/status'
import { Button, EmptyState, KeyValue, PlatformMark, type KeyValueItem } from '@/ui'
import { Section, durationText, enabledPlatforms, hasChannels, modeLabel, spacing } from './shared'

/** The edit button of a settings card: "편집" on screen, the card's name for
 *  a screen reader. */
function EditButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Button size="sm" variant="ghost" icon={Pencil} aria-label={label} onClick={onClick}>
      편집
    </Button>
  )
}

const onOff = (v: boolean) => (v ? '켬' : '끔')

/** 페이싱, read only: the rules that give an approved draft its slot, and the
 *  account each platform posts with. 편집 opens the form. */
export function PacingSummary({ c, targets, onEdit }: { c: Campaign; targets: Target[]; onEdit: () => void }) {
  const start = c.PostingWindowStart ?? ''
  const end = c.PostingWindowEnd ?? ''
  const space = spacing(c.PostsPerDay, start, end)
  const shown = enabledPlatforms(c)
  const platforms = shown.length > 0 ? shown : Object.keys(c.PlatformConfigs ?? {})
  const items: KeyValueItem[] = [
    ...(c.Mode !== 'affiliate' ? [{ label: '모드', value: modeLabel(c.Mode) }] : []),
    { label: '하루 글 수', value: `${c.PostsPerDay}건` },
    { label: '게시 창', value: start && end ? `${start}–${end}` : '하루 종일' },
    ...(c.PostsPerDay > 1
      ? [
          {
            label: '글 간격',
            value: (
              <>
                {durationText(space.minutes)}
                {!space.fits && (
                  <span className={`block text-meta ${TONE_CLASS.failed.text}`}>하루 {c.PostsPerDay}건은 이 창에 다 들어가지 않는다</span>
                )}
              </>
            ),
          },
        ]
      : []),
    { label: '시간대', value: c.Timezone || '—' },
    // A Coupang campaign shows its switch under 쿠팡 글감.
    ...(c.Mode !== 'affiliate' ? [{ label: '자동 승인', value: onOff(!!c.AutoApprove) }] : []),
  ]
  for (const p of platforms) {
    const cfg = c.PlatformConfigs[p]
    const t = targets.find((x) => x.id === cfg?.target_id)
    items.push({
      label: '계정',
      value: (
        <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
          <PlatformMark platform={p} />
          {t ? t.name : <span className={TONE_CLASS.publishing.text}>계정 미지정</span>}
        </span>
      ),
    })
    if (hasChannels(p)) {
      if (Number(cfg?.channel_cooldown_hours) > 0) items.push({ label: '채널 간격', value: `${Number(cfg?.channel_cooldown_hours)}시간` })
      const banned = Object.values(cfg?.channel_rules ?? {}).reduce((a, r) => a + (r.banned_phrases?.length ?? 0), 0)
      if (banned > 0) items.push({ label: '금지 문구', value: `${banned}개` })
    }
  }
  if (platforms.length === 0) items.push({ label: '계정', value: '—' })

  return (
    <Section title="페이싱" actions={<EditButton label="페이싱 편집" onClick={onEdit} />}>
      <KeyValue items={items} />
    </Section>
  )
}

/** 쿠팡 글감, read only: what a Coupang campaign writes about and how. */
export function AffiliateSummary({ c, onEdit }: { c: Campaign; onEdit: () => void }) {
  const a = c.Affiliate
  const cats = useAffiliateCategories()
  const avatars = useAffiliateAvatars().data?.items ?? []
  const names = (a?.category_ids ?? []).map((id) => cats.data?.items.find((x) => x.id === id)?.name ?? `#${id}`)
  if (a?.include_unassigned) names.push('공용')
  const persona = a?.persona ? (avatars.find((x) => x.key === a.persona)?.label ?? a.persona) : '계정 말투 따름'
  const items: KeyValueItem[] = a
    ? [
        { label: '카테고리', value: names.length > 0 ? names.join(' · ') : '—' },
        { label: '글 톤', value: toneLabel(a.tone) },
        { label: '말투', value: persona },
        { label: '최저 점수', value: a.min_signal_score > 0 ? `${a.min_signal_score}점` : '가장 높은 것부터' },
        { label: '유효 시간', value: a.stale_age_hours > 0 ? `${a.stale_age_hours}시간` : '설정값 따름' },
        { label: '상품 이미지', value: a.attach_image ? '붙인다' : '안 붙인다' },
        { label: '자동 승인', value: onOff(a.auto_approve) },
        { label: '30분 전 알림', value: onOff(a.heads_up ?? true) },
      ]
    : [{ label: '설정', value: '아직 없다. 편집에서 카테고리를 고른다.' }]
  return (
    <Section title="쿠팡 글감" actions={<EditButton label="쿠팡 글감 편집" onClick={onEdit} />}>
      <KeyValue items={items} />
    </Section>
  )
}

/** 글 생성, read only: the voice and the ad judge of a campaign that writes
 *  with the generator. Both are set when the campaign is made. */
export function WritingSummary({ c }: { c: Campaign }) {
  return (
    <Section title="글 생성">
      <KeyValue
        items={[
          { label: '페르소나', value: c.Persona ? <span className="whitespace-pre-wrap">{c.Persona}</span> : '—' },
          ...(c.StyleGuide ? [{ label: '문체', value: <span className="whitespace-pre-wrap">{c.StyleGuide}</span> }] : []),
          {
            label: '광고 판정',
            value: `광고 느낌 ${c.JudgeThreshold}점 이하면 통과 (0 자연스러움 – 10 노골적 광고) · 다시 쓰기 ${c.JudgeMaxRetries}회까지`,
          },
        ]}
      />
    </Section>
  )
}

/** 주제 풀: the topics a topic campaign takes in turn. */
export function TopicPool({ c }: { c: Campaign }) {
  const pool = c.TopicPool ?? []
  return (
    <Section title="주제 풀" aside={pool.length > 0 ? `${pool.length}개` : undefined} label="주제 풀">
      <p className="text-table text-muted-foreground">
        {c.ExpandTopics ? `주제를 ${c.TopicAnglesCount ?? 3}개 각도로 넓혀 생성한다.` : '주제를 그대로 생성한다.'}
        {pool.length > 0 && ` 다음 차례는 ${((c.TopicRotationState ?? 0) % pool.length) + 1}번째다.`}
      </p>
      {pool.length === 0 ? (
        <EmptyState title="주제가 없다" description="주제 풀은 캠페인을 만들 때 정한다. AI 초안 쓰기에서 주제를 직접 넣을 수도 있다." />
      ) : (
        <ol className="divide-y divide-border">
          {pool.map((topic, i) => (
            <li key={i} className="flex gap-3 py-1.5 text-body">
              <span className="w-5 shrink-0 text-right tabular-nums text-muted-foreground">{i + 1}</span>
              <span className="min-w-0 break-words">{topic}</span>
            </li>
          ))}
        </ol>
      )}
    </Section>
  )
}
